import { FC, useEffect, useMemo } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
	getDownloadGpsrInvoiceLoading,
	getGpsrInvoicesList,
	getGpsrInvoicesListLoad,
	getGpsrProductTypes,
} from 'redux/reducers/gpsr/selectors';
import {
	downloadGpsrInvoiceRequest,
	getGpsrInvoicesListRequest,
	getGpsrProductTypesRequest,
} from 'redux/reducers/gpsr/reducer';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { PAGE_SIZE } from 'services/constants/env';
import { KeyboardBackspace } from '@mui/icons-material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { PATHS } from 'routes/constants';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import queryString from 'query-string';
import { TFilter, TOrderBy } from 'redux/types';
import { ESortDirection } from 'ui/EasTable/types';
import { DATES_ARRAY } from 'services/constants/dates-constants';
import { tableStructure } from 'components/ReferralsPartners/tableStructure';
import { gpsrInvoiceListTableStructure } from './gpsr-invoice-list-table-structure';

export const GpsrInvoicesList: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();
	const navigateParams = queryString.parse(search);
	const params = useParams<{ emId?: string; productTypeId?: string }>();
	const { page } = queryString.parse(search);

	const savedUrl = useSelector(getSavedUrl);
	const invoicesList = useSelector(getGpsrInvoicesList);
	const invoicesListLoad = useSelector(getGpsrInvoicesListLoad);
	const gpsrData = useSelector(getGpsrProductTypes);
	const downloadGpsrInvoiceLoading = useSelector(getDownloadGpsrInvoiceLoading);

	const adaptedInvoicesList = useMemo(() => {
		if (!invoicesList) return null;
		return {
			...invoicesList,
			data: invoicesList.data.map((item) => ({
				...item,
				id: item.invoiceId,
			})),
		};
	}, [invoicesList]);

	const navigateParamsWithoutPage = gpsrInvoiceListTableStructure.reduce((acc, item) => {
		if (item.selector === 'emId' && params.emId) {
			acc.push({
				fieldName: 'emId',
				value: params.emId,
				operator: '=',
			});
		}

		if (!!navigateParams[item.selector] && item.type === 'date') {
			const dates = DATES_ARRAY.find((i) => i.id === navigateParams[item.selector]);
			if (dates?.from) {
				acc.push({
					fieldName: item.selector,
					value: dates.from,
					operator: '>=',
				});
			}
			if (dates?.to) {
				acc.push({
					fieldName: item.selector,
					value: dates.to,
					operator: '<=',
				});
			}
			return acc;
		}
		if (navigateParams[item.selector])
			acc.push({
				fieldName: item.selector,
				value: navigateParams[item.selector] as string,
				operator: '=',
			});
		return acc;
	}, [] as TFilter[]);

	useEffect(() => {
		const orderBy: TOrderBy[] = [];
		if (navigateParams?.sortIndex) {
			orderBy.push({
				fieldName: navigateParams?.sortIndex
					? tableStructure[+navigateParams.sortIndex].selector
					: 'id',
				direction: navigateParams?.sortDirection
					? String(navigateParams.sortDirection) === 'desc'
						? ESortDirection.DESC
						: ESortDirection.ASC
					: ESortDirection.DESC,
			});
		}
		orderBy.push({
			fieldName: 'id',
			direction: ESortDirection.DESC,
		});
		dispatch(
			getGpsrInvoicesListRequest({
				pageNumber: page ? +page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(navigateParams?.search ?? ''),
				filters: navigateParamsWithoutPage,
				orderBy,
			}),
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, search]);

	useEffect(() => {
		if (params.emId) {
			dispatch(getGpsrProductTypesRequest({ emId: +params.emId, searchString: '' }));
		}
	}, [dispatch, params.emId, search]);

	const handleReplayHistory = () => {
		if (savedUrl) {
			if (savedUrl.includes(PATHS.GPSR_PAGE) && !savedUrl.includes(`/invoices`)) navigate(savedUrl);
			else if (params.emId) navigate(`${PATHS.GPSR_PAGE}/${params.emId}`);
			else navigate(PATHS.GPSR_PAGE);
		}
	};

	const handleAction = (id: number) => {
		if (params.emId) {
			dispatch(downloadGpsrInvoiceRequest({ emId: +params.emId, invoiceId: id }));
		}
	};

	return (
		<Box
			sx={{
				margin: '0px 10px 20px 10px',
				maxWidth: 'calc(100vw - 20px)',
				overflowX: 'auto',
			}}
		>
			<Box sx={{ width: 'auto', minWidth: '800px' }}>
				<EasTableWithFilter
					tableStructure={gpsrInvoiceListTableStructure}
					items={adaptedInvoicesList}
					itemsIsLoading={invoicesListLoad || downloadGpsrInvoiceLoading}
					handleAction={handleAction}
				>
					<Box
						sx={{
							backgroundColor: 'primary.light',
							display: 'flex',
							justifyContent: 'start',
							alignItems: 'center',
							gap: '10px',
							cursor: 'pointer',
						}}
						onClick={handleReplayHistory}
					>
						<IconButton
							color="primary"
							sx={{
								p: '0',
							}}
						>
							<KeyboardBackspace />
						</IconButton>
						<Typography
							sx={{
								fontSize: '.9rem',
								color: 'primary.main',
								fontWeight: 'bold',
							}}
						>
							{params.emId && ` ${gpsrData?.merchant.emName ?? ''}`}
						</Typography>
						<Divider orientation="vertical" flexItem color="primary.main" />
						<Typography
							sx={{
								fontSize: '.9rem',
								color: 'primary.main',
								fontWeight: 'bold',
							}}
						>
							GPSR Invoices
						</Typography>
					</Box>
				</EasTableWithFilter>
			</Box>
		</Box>
	);
};
